import { Card, Eyebrow, SectionLabel, Text, TextField, Title2, WasabiButton } from '@kk/ui';
import { useState } from 'react';
import { useOutletContext } from 'react-router';

import { useMembership } from '@/auth/SessionProvider';
import { ColumnHead, LinkButton, Status } from '@/components/bits';
import { CodeOnHoldNote, useCodesOnHold } from '@/components/CodeOnHold';
import { Sheet } from '@/components/Sheet';
import { deck } from '@/copy/deck';
import { placeholders } from '@/copy/placeholders';
import {
  useErrorMessage,
  useCreateGroup,
  useGenerateCode,
  useGroups,
  useInviteStaff,
  useRevokeCode,
  useRevokeInvite,
  useStaff,
} from '@/data/hooks';
import { monthDay, relativeDay } from '@/lib/time';
import type { ShellContext } from '@/shell/Shell';
import { portalLayout } from '@/theme/portalTokens';
import type { OrgGroupDetail, OrgSummary, StaffInvite, StaffRole } from '@/types/wire';

const JOIN_CODE_SIZE = 30; // the `score` variant (30/800) carries the code; tracking is .14em of it

/** "Expires 31 Dec · 6 uses left of 20" from the wire's optional halves. */
export function codeLine(code: NonNullable<OrgGroupDetail['joinCode']>): string {
  return deck.o05.codeLine(
    code.expiresAt ? monthDay(code.expiresAt) : undefined,
    code.max !== undefined ? Math.max(0, code.max - code.uses) : undefined,
    code.max,
  );
}

function roleWord(role: StaffRole): string {
  return role === 'orgAdmin' ? deck.o05.role.orgAdmin : deck.o05.role.groupAdmin;
}

function GroupCard({ group }: { group: OrgGroupDetail }) {
  const errorMessage = useErrorMessage();
  // DECISIONS 93.2: before billing (or while paused) the code is drawn dimmed, with why and the button that fixes it
  const onHold = useCodesOnHold();
  const generate = useGenerateCode();
  const revoke = useRevokeCode();
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const code = group.joinCode?.active ? group.joinCode : undefined;
  const names = group.staff.map((s) => s.displayName).filter((n): n is string => !!n);
  const run = async (p: Promise<unknown>) => {
    setError(null);
    try {
      await p;
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setConfirming(false);
    }
  };
  return (
    <Card variant={code && !onHold ? 'playfulBold' : 'quiet'} radius="row" padding={18}>
      <div className="stack" data-testid={`group-${group.id}`}>
        <div className="cell">
          <Text variant="cardTitle" tone="ink">
            {group.name}
          </Text>
          <Text variant="meta" tone="muted">
            {deck.o05.meta(group.childCount, group.staff.length, names)}
          </Text>
        </div>
        {code ? (
          <>
            <div
              className={`code-panel${onHold ? ' on-hold' : ''}`}
              data-testid={`code-panel-${group.id}`}
              aria-disabled={onHold ? true : undefined}
            >
              <Eyebrow tone="muted">{deck.o05.joinCode}</Eyebrow>
              <Text
                variant="score"
                tone="ink"
                style={{ letterSpacing: JOIN_CODE_SIZE * portalLayout.joinCodeTrackingEm }}
                testID={`code-${group.id}`}
              >
                {code.code}
              </Text>
              {codeLine(code) ? (
                <Text variant="time" tone="muted">
                  {codeLine(code)}
                </Text>
              ) : null}
            </div>
            {onHold ? <CodeOnHoldNote reason={onHold} testID={`code-on-hold-${group.id}`} /> : null}
            <div className="button-pair">
              {confirming ? (
                <>
                  <WasabiButton
                    label={placeholders.deny.cancel}
                    variant="outline"
                    textSize="sm"
                    faceHeight={44}
                    onPress={() => setConfirming(false)}
                  />
                  <WasabiButton
                    label={deck.o05.revoke}
                    size="sm"
                    loading={revoke.isPending}
                    onPress={() => void run(revoke.mutateAsync(group.id))}
                    testID={`revoke-confirm-${group.id}`}
                  />
                </>
              ) : (
                // Print poster is gone until a poster exists (the 23 Oct audit, T2-13): O05 does not draw it, and a
                // greyed button promised something nobody could press
                <WasabiButton
                  label={deck.o05.revoke}
                  variant="outline"
                  textSize="sm"
                  faceHeight={44}
                  onPress={() => setConfirming(true)}
                  testID={`revoke-${group.id}`}
                />
              )}
            </div>
          </>
        ) : (
          <>
            <div className="no-code">
              <Text variant="bodySm" tone="ink">
                {deck.o05.noCode}
              </Text>
            </div>
            {/* a new code would be refused until billing allows it, so the button that fixes billing stands instead */}
            {onHold ? (
              <CodeOnHoldNote reason={onHold} withoutLine testID={`code-on-hold-${group.id}`} />
            ) : (
              <WasabiButton
                label={deck.o05.generateCode}
                size="sm"
                loading={generate.isPending}
                onPress={() => void run(generate.mutateAsync({ groupId: group.id, input: {} }))}
                testID={`generate-${group.id}`}
              />
            )}
          </>
        )}
        {error ? <Status testID={`group-error-${group.id}`}>{error}</Status> : null}
      </div>
    </Card>
  );
}

function NewGroupSheet({ org, onClose }: { org: OrgSummary | undefined; onClose: () => void }) {
  const errorMessage = useErrorMessage();
  const create = useCreateGroup();
  const [name, setName] = useState('');
  const [branchId, setBranchId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const submit = async () => {
    if (!name.trim()) {
      setError(deck.o05.nameNeeded);
      return;
    }
    setError(null);
    try {
      await create.mutateAsync({ name: name.trim(), ...(branchId ? { branchId } : {}) });
      onClose();
    } catch (e) {
      setError(errorMessage(e));
    }
  };
  return (
    <Sheet
      title={
        <Text variant="cardTitle" tone="ink">
          {deck.o05.newGroup}
        </Text>
      }
      onClose={onClose}
      testID="new-group-sheet"
    >
      <form
        className="sheet-form"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <TextField
          label={placeholders.groups.nameField}
          value={name}
          onChangeText={setName}
          testID="group-name"
        />
        {org && org.branches.length > 0 ? (
          <select
            className="field-select"
            aria-label={placeholders.groups.branchField}
            value={branchId}
            onChange={(e) => setBranchId(e.target.value)}
            data-testid="group-branch"
          >
            <option value="">{placeholders.groups.noBranch}</option>
            {org.branches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        ) : null}
        {error ? <Status>{error}</Status> : null}
        <WasabiButton
          label={deck.o05.newGroup}
          loading={create.isPending}
          onPress={() => void submit()}
          testID="create-group"
        />
        <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
      </form>
    </Sheet>
  );
}

function InfoCard({ org }: { org: OrgSummary | undefined }) {
  return (
    <Card variant="quiet" radius="row" padding={18}>
      <div className="info-card">
        <Text variant="bodySm" tone="slate">
          <Text variant="bodySmBold" tone="ink">
            {deck.o05.info.staff}
          </Text>{' '}
          {deck.o05.info.staffText}
        </Text>
        {/*
          Only at two or more (Evan, 2026-09-16): the switcher this line explains does not render
          below two, so at one branch — or none — it describes a control that is not on the screen,
          and "this org has 0 branches" was what it actually said.
        */}
        {(org?.branches.length ?? 0) >= 2 ? (
          <Text variant="bodySm" tone="slate">
            <Text variant="bodySmBold" tone="ink">
              {deck.o05.info.branches}
            </Text>{' '}
            {deck.o05.info.branchesText(org!.branches.length)}
          </Text>
        ) : null}
        <Text variant="bodySm" tone="slate">
          <Text variant="bodySmBold" tone="ink">
            {deck.o05.info.codes}
          </Text>{' '}
          {deck.o05.info.codesText}
        </Text>
      </div>
    </Card>
  );
}

function InviteForm({
  org,
  onInvited,
}: {
  org: OrgSummary | undefined;
  onInvited: (i: StaffInvite) => void;
}) {
  const errorMessage = useErrorMessage();
  const invite = useInviteStaff();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<StaffRole>('groupAdmin');
  const [groupIds, setGroupIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const submit = async () => {
    const address = email.trim().toLowerCase();
    if (!address.includes('@')) {
      setError(deck.o05.emailNeeded);
      return;
    }
    setError(null);
    try {
      const created = await invite.mutateAsync({
        email: address,
        role,
        ...(role === 'groupAdmin' ? { groupIds } : {}),
      });
      setEmail('');
      setGroupIds([]);
      onInvited(created);
    } catch (e) {
      setError(errorMessage(e));
    }
  };
  return (
    <form
      className="stack"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
      data-testid="invite-form"
    >
      <div className="invite-form">
        <TextField
          label={deck.signIn.email}
          value={email}
          onChangeText={setEmail}
          autoCapitalize="none"
          keyboardType="email-address"
          testID="invite-email"
        />
        <select
          className="field-select"
          aria-label={placeholders.staff.role}
          value={role}
          onChange={(e) => setRole(e.target.value as StaffRole)}
          data-testid="invite-role"
        >
          <option value="groupAdmin">{deck.o05.role.groupAdmin}</option>
          <option value="orgAdmin">{deck.o05.role.orgAdmin}</option>
        </select>
        <WasabiButton
          label={placeholders.staff.invite}
          size="sm"
          fullWidth={false}
          loading={invite.isPending}
          onPress={() => void submit()}
          testID="invite-submit"
        />
      </div>
      {role === 'groupAdmin'
        ? (org?.groups ?? []).map((g) => (
            <label className="check-row" key={g.id}>
              <input
                type="checkbox"
                checked={groupIds.includes(g.id)}
                onChange={() =>
                  setGroupIds((ids) =>
                    ids.includes(g.id) ? ids.filter((x) => x !== g.id) : [...ids, g.id],
                  )
                }
                data-testid={`invite-group-${g.id}`}
              />
              <Text variant="rowTitleSm" tone="ink">
                {g.name}
              </Text>
            </label>
          ))
        : null}
      {error ? <Status testID="invite-error">{error}</Status> : null}
      <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
    </form>
  );
}

function StaffSection({ org }: { org: OrgSummary | undefined }) {
  const errorMessage = useErrorMessage();
  const staff = useStaff();
  const revoke = useRevokeInvite();
  const [lastInvite, setLastInvite] = useState<StaffInvite | null>(null);
  const [error, setError] = useState<string | null>(null);
  const members = staff.data?.members ?? [];
  const invites = (staff.data?.invites ?? []).filter((i) => i.status === 'pending');
  const groupName = (id: string) => org?.groups.find((g) => g.id === id)?.name ?? '';
  const groupsOf = (ids: string[], role: StaffRole) =>
    role === 'orgAdmin'
      ? deck.shell.orgAdmin
      : deck.roster.joinNames(ids.map(groupName).filter(Boolean));
  const status = staff.isPending
    ? placeholders.empty.loading
    : staff.error
      ? errorMessage(staff.error)
      : members.length + invites.length === 0
        ? placeholders.staff.empty
        : null;
  return (
    <Card variant="quiet" radius="row" padding={18}>
      <div className="stack">
        <SectionLabel>{deck.o05.info.staff}</SectionLabel>
        <div className="table-scroll">
          <div className="grid-head grid-staff">
            <ColumnHead>{placeholders.staff.columns.name}</ColumnHead>
            <ColumnHead>{placeholders.staff.columns.role}</ColumnHead>
            <ColumnHead>{deck.roster.columns.groups}</ColumnHead>
            <ColumnHead>{placeholders.staff.columns.sent}</ColumnHead>
          </div>
          {members.map((m) => (
            <div className="grid-row grid-staff" key={m.id} data-testid={`member-${m.id}`}>
              <div className="cell">
                <Text variant="rowTitleSm" tone="ink" numberOfLines={1}>
                  {m.displayName ?? m.email ?? m.userId}
                </Text>
                {m.email && m.displayName ? (
                  <Text variant="meta" tone="muted" numberOfLines={1}>
                    {m.email}
                  </Text>
                ) : null}
              </div>
              <Text variant="bodySm" tone="slate">
                {roleWord(m.role)}
              </Text>
              <Text variant="meta" tone="slate" numberOfLines={2}>
                {groupsOf(m.groupIds, m.role)}
              </Text>
              <span />
            </div>
          ))}
          {invites.map((i) => (
            <div className="grid-row grid-staff invite" key={i.id} data-testid={`invite-${i.id}`}>
              <div className="cell">
                <Text variant="rowTitleSm" tone="ink" numberOfLines={1}>
                  {i.email}
                </Text>
                <Text variant="meta" tone="muted">
                  {placeholders.staff.pending}
                </Text>
              </div>
              <Text variant="bodySm" tone="slate">
                {roleWord(i.role)}
              </Text>
              <Text variant="meta" tone="slate" numberOfLines={2}>
                {groupsOf(i.groupIds, i.role)}
              </Text>
              <div className="cell-inline">
                <Text variant="meta" tone="muted">
                  {relativeDay(i.sentAt)}
                </Text>
                <LinkButton
                  label={deck.o05.revoke}
                  disabled={revoke.isPending}
                  onPress={() => {
                    setError(null);
                    revoke.mutateAsync(i.id).catch((e: unknown) => setError(errorMessage(e)));
                  }}
                  testID={`revoke-invite-${i.id}`}
                />
              </div>
            </div>
          ))}
        </div>
        {status ? <Status>{status}</Status> : null}
        {error ? <Status testID="staff-error">{error}</Status> : null}
        <InviteForm org={org} onInvited={setLastInvite} />
        {lastInvite?.acceptToken ? (
          // Dev only: the API returns the accept token in the body while no email service is configured. It is
          // shown as text to copy, never as a link.
          <div className="cell">
            <Text variant="label" tone="slate">
              {placeholders.staff.devToken}
            </Text>
            <input
              className="token-field"
              readOnly
              value={lastInvite.acceptToken}
              aria-label={placeholders.staff.devToken}
              onFocus={(e) => e.currentTarget.select()}
              data-testid="accept-token"
            />
          </div>
        ) : null}
      </div>
    </Card>
  );
}

/** O05 · /groups — org admins only; a group admin sees one placeholder line and no request is made. */
export function GroupsScreen() {
  const errorMessage = useErrorMessage();
  const { org } = useOutletContext<ShellContext>();
  const membership = useMembership();
  const orgAdmin = membership?.role === 'orgAdmin';
  const groups = useGroups(orgAdmin);
  const [creating, setCreating] = useState(false);

  if (!orgAdmin) return <Status testID="org-admin-only">{placeholders.groups.orgAdminOnly}</Status>;

  const rows = groups.data ?? [];
  const status = groups.isPending
    ? placeholders.empty.loading
    : groups.error
      ? errorMessage(groups.error)
      : rows.length === 0
        ? placeholders.groups.empty
        : null;

  return (
    <>
      <div className="page-header">
        <div className="page-header-text">
          <Title2 tone="ink">{deck.o05.title}</Title2>
          <Text variant="bodySm" tone="muted">
            {deck.o05.subtitle}
          </Text>
        </div>
        <div className="actions">
          <WasabiButton
            label={deck.o05.newGroup}
            size="sm"
            fullWidth={false}
            onPress={() => setCreating(true)}
            testID="new-group"
          />
        </div>
      </div>
      {status ? <Status>{status}</Status> : null}
      <div className="group-cards">
        {rows.map((g) => (
          <GroupCard key={g.id} group={g} />
        ))}
      </div>
      <InfoCard org={org} />
      <StaffSection org={org} />
      {creating ? <NewGroupSheet org={org} onClose={() => setCreating(false)} /> : null}
    </>
  );
}
